import React, { useEffect, useRef, useState } from 'react';
import cn from '../../lib/cn';
import observeReveal from '../../lib/revealManager';

/**
 * A single line of type that slides up from behind its own edge.
 *
 * The mask is an `overflow-hidden` wrapper. Because descenders and the hero's
 * hand-drawn underline sit below the text baseline, the wrapper is padded and
 * then negatively margined by the same amount — that gives the glyphs room
 * without the mask clipping them or the line changing the layout.
 */
export function RevealLine({
  children,
  delay = 0,
  duration = 900,
  className,
  bleed = '0.35em',
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => observeReveal(ref.current, () => setShown(true)), []);

  return (
    <span
      ref={ref}
      className={cn('block overflow-hidden', className)}
      style={{ paddingBottom: bleed, marginBottom: `-${bleed}` }}
    >
      <span
        className="block"
        style={{
          transform: shown ? 'none' : 'translate3d(0, 105%, 0)',
          opacity: shown ? 1 : 0,
          transition:
            `transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, ` +
            `opacity ${Math.round(duration * 0.6)}ms ease ${delay}ms`,
          willChange: shown ? 'auto' : 'transform, opacity',
        }}
      >
        {children}
      </span>
    </span>
  );
}

/**
 * Multi-line heading built from `RevealLine`s, each offset from the last.
 *
 * Pass `lines` as an array of strings — one entry per visual line. This
 * replaces a `<br />` inside a heading, because a mask has to wrap each line
 * individually to slide them independently.
 *
 * @param {object}   props
 * @param {string[]} props.lines
 * @param {number}   props.step  ms between successive lines
 */
export function TextReveal({
  as: Tag = 'span',
  lines = [],
  delay = 0,
  step = 110,
  duration,
  className,
  lineClassName,
}) {
  return (
    <Tag className={className}>
      {lines.map((line, i) => (
        <RevealLine
          key={`${line}-${i}`}
          delay={delay + i * step}
          duration={duration}
          className={lineClassName}
        >
          {line}
        </RevealLine>
      ))}
    </Tag>
  );
}

export default TextReveal;
